import { format } from 'date-fns';
import throttle from 'lodash.throttle';
import { originalConsole } from '../logging';
const EVENT_PAGE_UNLOAD = 'custom-page-unload';
/**
 * Portal transport for logging. NOTE: The logger will collect logs without loggerUrl set
 * but won't send them until loggerUrl is set.
 */
export class DataPortalTransport {
    constructor(loggerUrl) {
        this.id = DataPortalTransport.id;
        this.logArr = [];
        this.unloading = false;
        // Note - it is totally fine to instantiate the transport WITHOUT the loggerUrl as it'll just
        // add entries to logArr and send them as soon as loggerUrl is set via setLoggerUrl.
        if (loggerUrl) {
            this.loggerUrl = loggerUrl;
        }
        this.listenSendOnPageUnload();
        // Send log arr maximum of once per 30 seconds.
        this.throttledSendLogArr = throttle(this.sendLogArr.bind(this), 30000);
    }
    setLoggerUrl(loggerUrl) {
        this.loggerUrl = loggerUrl;
    }
    listenSendOnPageUnload() {
        const event = new Event(EVENT_PAGE_UNLOAD);
        document.addEventListener('visibilitychange', () => {
            if (this.unloading) {
                // Unload has been triggered by another event.
                return;
            }
            if (document.visibilityState === 'hidden') {
                this.unloading = true;
                window.dispatchEvent(event);
            }
        });
        window.addEventListener('pagehide', () => {
            if (this.unloading) {
                // Unload has been triggered by another event.
                return;
            }
            this.unloading = true;
            window.dispatchEvent(event);
        });
        window.addEventListener('beforeunload', () => {
            if (this.unloading) {
                // Unload has been triggered by another event.
                return;
            }
            this.unloading = true;
            window.dispatchEvent(event);
        });
        window.addEventListener(EVENT_PAGE_UNLOAD, async () => {
            await this.sendLogArr(); // Do not throttle, just send!
            this.unloading = false;
        });
    }
    async sendToLoggerUrl(data, attempt) {
        const maxAttempts = 3;
        const attemptCounter = (attempt !== null && attempt !== void 0 ? attempt : 0) + 1;
        // Note - we are using fetch over axios because axios does not support keepalive.
        // We need keepalive to be true in case someone closes the page or the browser.
        // Ideally we should also be using sendBeacon on page unload, but it doesn't support PUT requests.
        // Not sure why the logger endpoint expects PUT over POST.
        if (!this.loggerUrl) {
            return false;
        }
        try {
            await fetch(this.loggerUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'text/plain' // Note, core_pod has this as an empty string.
                },
                body: data,
                keepalive: true // Important for ensuring the request is sent even when the page is unloading.
            });
        }
        catch (_a) {
            if (attemptCounter < maxAttempts) {
                return this.sendToLoggerUrl(data, attemptCounter);
            }
            else {
                originalConsole.error(`ERROR: Failed to send logs to ${this.loggerUrl}`);
            }
        }
        return true;
    }
    async sendLogArr() {
        if (!this.loggerUrl) {
            originalConsole.warn('Unable to send logs to logger endpoint as loggerUrl has not been set');
            return;
        }
        if (!this.logArr.length) {
            // Nothing to send.
            return;
        }
        const flatLogs = this.logArr.join('\n');
        // Clear logs
        this.logArr = [];
        // Send the logs.
        return this.sendToLoggerUrl(flatLogs);
    }
    log(loggerName, level, ...args) {
        if (!this.loggerUrl && this.logArr.length > 1000) {
            this.logArr.shift();
        }
        // Note - I think we only include the time in the log because the s3 bucket directory
        // defined by this.loggerUrl includes the date.
        const time = format(new Date(), 'HH:mm:ss');
        const levelUpper = level.toUpperCase();
        const flatArgs = args
            .map((arg) => {
            try {
                return JSON.stringify(arg).substring(0, 1000);
            }
            catch (_a) {
                return String(arg).substring(0, 1000);
            }
        })
            .join(' ');
        const logRow = `${time}:${levelUpper}: ${flatArgs}`;
        this.logArr.push(logRow);
        if (this.loggerUrl) {
            if (this.logArr.length > 250) {
                // If more than 250 log rows in buffer, then send straight away.
                this.sendLogArr();
            }
            else {
                // If not, throttle send.
                this.throttledSendLogArr();
            }
        }
    }
}
DataPortalTransport.id = Symbol('Portal');
